import styled from '@emotion/styled'
import { useFocusTrap } from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacOmaLogo } from '@pharmac/ui'
import { PropsWithChildren, useState } from 'react'
import { NavigationMenu, NavigationMenuItemProps } from './menu/NavigationMenu'
import { ShoppingCartButton } from './shopping-cart/ShoppingCartButton'
import { NavigationLink, useExtranetLinks } from './useExtranetLinks'
import { UserMenu } from './user-menu/UserMenu'

const MobileNavigationRoot = styled.div`
  position: relative;
  width: 100%;
`

const MobileNavigationInner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`

const MobileNavigationHamburger = styled.button`
  background-color: none;
  border: 0;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: calc(30px + var(--spacing-medium) * 2);

  margin-right: var(--spacing-small);

  svg {
    color: var(--color-white);
  }
`

const MobileNavigationLogo = styled.div`
  width: 75px;
`

const MobileNavigationUserMenu = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: auto;
  margin-right: var(--spacing-medium);
`

const MobileNavigationMenu = styled.div`
  position: absolute;
  overflow: hidden;

  transition: max-height 0.3s var(--ease-cubic-bezier);

  top: 100%;
  left: 0px;
  z-index: 200;

  width: 100%;

  background: var(--extranet-menu-bg-gradient);
`

const MobileMenuFocusTrap = ({
  onMouseLeave,
  children,
}: PropsWithChildren & { onMouseLeave: () => void }) => {
  const [hasFocus, setHasFocus] = useState(true)

  useFocusTrap(hasFocus, onMouseLeave)

  return (
    <div
      onMouseEnter={() => setHasFocus(true)}
      onMouseLeave={() => setHasFocus(false)}
    >
      {children}
    </div>
  )
}

const mapLinksToMenuItems = (links: NavigationLink[]): NavigationMenuItemProps[] => {
  const mapLinkToMenuItem = (a: NavigationLink): NavigationMenuItemProps => {
    const hasChildren = !!a.children && a.children.length > 0

    return {
      label: a.label,
      navigateTo: !hasChildren ? a.path : undefined,
      children: hasChildren ? a.children?.map(mapLinkToMenuItem) : undefined,
      icon: a.icon,
    }
  }

  const linksWithLabels = links.filter((a) => !!a.label)

  return linksWithLabels.map(mapLinkToMenuItem)
}

export const MobileNavigation = () => {
  const [state, setState] = useState(false)

  const toggle = () => setState(!state)
  const close = () => setState(false)

  const links = useExtranetLinks()

  const menu: NavigationMenuItemProps[] = mapLinksToMenuItems(links)

  return (
    <MobileNavigationRoot>
      <MobileNavigationInner>
        <MobileNavigationHamburger
          title="Open navigation"
          onClick={toggle}
        >
          <PharmacIcon
            icon={state ? 'x' : 'menu'}
            size="30"
          />
        </MobileNavigationHamburger>
        <MobileNavigationLogo>
          <PharmacOmaLogo
            color="white"
            width="75px"
          />
        </MobileNavigationLogo>
        <MobileNavigationUserMenu>
          <ShoppingCartButton />
          <UserMenu noCustomer />
        </MobileNavigationUserMenu>
      </MobileNavigationInner>
      <MobileMenuFocusTrap onMouseLeave={close}>
        <MobileNavigationMenu
          style={{
            maxHeight: state ? '3000px' : '0px',
            padding: state ? 'var(--spacing-large) 0' : '0 0',
          }}
        >
          <NavigationMenu
            xl
            clickCallback={close}
            menu={menu}
          />
        </MobileNavigationMenu>
      </MobileMenuFocusTrap>
    </MobileNavigationRoot>
  )
}
